import React, { Component } from "react";
import cn from "classnames";
import store from "store";

import { taskTypes, aspectRatio as ar } from "../../../common/parameters";
import Seo from "../../../common/helpers/seo";
import getName, { getNameLang } from "../../../common/helpers/getName";
import getPlanSeo from "../../../common/helpers/getPlanSeo";
import { checkPlayable } from "../../../common/parameters";

import routing from "../../routing";
import Icon from "../../icon";
import NotFoundContent from "../../components/NotFoundContent";
import { PageNumber, PagingButton } from "../../components/Paging";
import PackName from "../../components/PackName";
import Breadcrumb from "../../components/Breadcrumb";
import PageLoader from "../../components/PageLoader";

import PlayContainer from "./PlayContainer";
import EditorContainer from "./EditorContainer";
import TutorialContainer from "./TutorialContainer";

import { BackBtn, Editor, Wrapper, Pagination, TutorialButton, Gap } from "./styles";
import messages from "./messages";
import { IProps, IState } from "./interfaces";

class Task extends Component<IProps, IState> {
    wrapper?: HTMLDivElement;
    constructor(props: IProps) {
        super(props);
        const { resetPlan, match, plan, history } = props;
        const { params } = match;
        if (plan && plan._id !== params.id) {
            resetPlan(() => null);
        }
        let publicView =
            props.history.location.pathname.slice(1, routing.publicPlan.length + 1) === routing.publicPlan ||
            props.history.location.pathname.slice(1, routing.testModeration.length + 1) === routing.testModeration;
        let inlineEditorOpen = false;
        let playable = true;
        let taskIndex = +params.index - 1;
        if (!plan) {
            props.getPlan(props.history.location.pathname, res => {
                const { data } = res;
                let taskIndex = this.state.taskIndex;
                if (data.found && data.plan.tasks.length <= taskIndex) {
                    taskIndex = data.plan.tasks.length - 1;
                    history.replace(`/${routing.plan}/${params.id}/${routing.tasks}/${taskIndex + 1}`);
                    this.setState({ taskIndex });
                }
                if (!data.found) {
                    this.setState({ notFoundModal: true });
                    return;
                }
                setTimeout(() => {
                    this.resize();
                });
            });
        } else {
            if (plan.tasks) {
                if (plan.tasks.length <= taskIndex) {
                    taskIndex = plan.tasks.length - 1;
                    history.replace(`/${routing.plan}/${plan._id}/${routing.tasks}/${taskIndex + 1}`);
                }
                playable = checkPlayable(plan.tasks[taskIndex], plan);
                if (!playable) {
                    inlineEditorOpen = true;
                }
            } else {
                inlineEditorOpen = true;
            }
            setTimeout(this.resize);
        }
        this.state = {
            flowPage: 0,
            taskIndex,
            planId: params.id,
            inlineEditorOpen,
            publicView,
            moderatorView:
                props.history.location.pathname.slice(1, routing.testModeration.length + 1) === routing.testModeration,
            viewProps: {},
            playable,
            isScaleAlert: false,
            notFoundModal: false,
            paging: false,
            isReloading: false,
        };

        if (process.env.WEBPACK) {
            if (!props.user.loggedIn && store.get("play-plan-id") !== this.state.planId) {
                props.history.replace(`/${routing.publicPlan}/${this.state.planId}`);
                return;
            }
        }
    }
    page = () => {
        this.setState({ paging: true }, () => {
            this.setState(
                { taskIndex: +this.props.match.params.index - 1, inlineEditorOpen: false, flowPage: 0 },
                () => {
                    this.setState({ paging: false });
                }
            );
        });
    };
    toggleInlineEditor = (inlineEditorOpen: boolean) => {
        this.setState({ inlineEditorOpen });
    };

    componentDidMount() {
        window.addEventListener("resize", this.resize);
        window.addEventListener("orientationchange", this.resize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
        window.removeEventListener("orientationchange", this.resize);
    }
    resize = () => {
        if (!this.wrapper) return;
        if (window.innerWidth < 992) {
            let height = window.innerHeight - 300 + 66;
            let titleHeight = 0;
            if (window.innerWidth <= 550) {
                titleHeight = 48;
                height -= 56;
            }
            let paddingTop = 0;
            paddingTop =
                (height - titleHeight - this.wrapper.getBoundingClientRect().width * (ar.height / ar.width)) / 2 +
                titleHeight;
            paddingTop = Math.max(paddingTop, 0);
            this.setState({ viewProps: { height, paddingTop } });
        } else {
            this.setState({ viewProps: { height: "", paddingTop: 0 } });
        }
    };

    componentDidUpdate() {
        const { taskIndex, inlineEditorOpen } = this.state;
        const { plan } = this.props;
        if (!plan || !plan.tasks) return;
        const task = plan.tasks[taskIndex];
        const playable = checkPlayable(task, plan);
        if (!playable && !inlineEditorOpen) {
            this.toggleInlineEditor(true);
        }
        if (this.state.playable !== playable) {
            this.setState({ playable });
        }
    }

    reload = () => {
        this.setState({ isReloading: true }, () => {
            this.setState({ isReloading: false });
        });
    };

    scaleAlert = (isScaleAlert: boolean) => {
        this.setState({ isScaleAlert });
    };

    changeFlowPage = (flowPage: number) => {
        this.setState({ flowPage });
    };

    render() {
        const { publicView, notFoundModal, viewProps, paging, taskIndex, playable, isScaleAlert, isReloading } = this.state;
        const {
            plan,
            intl: { formatMessage },
            intl,
            history,
        } = this.props;
        let { inlineEditorOpen, moderatorView, flowPage } = this.state;
        if (paging) return null;
        if (notFoundModal || plan?.notFound) {
            return (
                <Editor marginTop={50} className={cn("spring--shadow")} style={{ paddingTop: 0 }}>
                    <Seo title={formatMessage(messages.notFound)} type={"article"} noindex />
                    <NotFoundContent />
                </Editor>
            );
        }
        if (plan === null || taskIndex === null || plan.loading) return <PageLoader />;
        if (!plan.tasks) return null;
        const task = plan.tasks[taskIndex];
        if (!task || !task.type) return null;
        let TaskType = taskTypes(formatMessage)[task.type];
        const planSeo = getPlanSeo(plan, intl, publicView, history.location.pathname);
        const name = getName(plan.name, intl);
        const lang = getNameLang(plan.name, name)!;
        return (
            <Editor marginTop={50} className={cn("spring--shadow", "spring--padding")}>
                <Seo {...planSeo} noindex nofollow />
                <Wrapper ref={r => (this.wrapper = r!)}>
                    <BackBtn>
                        {!publicView ? (
                            <Breadcrumb
                                pack={<PackName pack={{ name: plan.packName! }} />}
                                packId={plan.pack}
                                plan={name}
                                planId={plan._id}
                                taskIndex={taskIndex}
                                taskType={task.type}
                                lang={lang}
                            />
                        ) : (
                            <Breadcrumb
                                moderatorView={moderatorView}
                                browse
                                plan={name}
                                planId={plan._id}
                                taskIndex={taskIndex}
                                taskType={task.type}
                                lang={lang}
                            />
                        )}
                    </BackBtn>
                    <Pagination>
                        <TutorialButton>
                            <TutorialContainer
                                type={task.type}
                                title={TaskType}
                            />
                        </TutorialButton>
                        <PagingButton
                            className={cn("button", "button--light")}
                            to={`/${
                                moderatorView ? routing.testModeration : publicView ? routing.publicPlan : routing.plan
                            }/${plan._id}/${routing.tasks}/${taskIndex}`}
                            onClick={this.page}
                            disabled={taskIndex === 0}
                            replace
                        >
                            <Icon i="arrowback" />
                        </PagingButton>
                        <PageNumber>{taskIndex + 1}</PageNumber>
                        <PagingButton
                            className={cn("button", "button--light", "Flip")}
                            to={`/${
                                moderatorView ? routing.testModeration : publicView ? routing.publicPlan : routing.plan
                            }/${plan._id}/${routing.tasks}/${taskIndex + 2}`}
                            onClick={this.page}
                            disabled={taskIndex >= plan.tasks.length - 1}
                            replace
                        >
                            <Icon i="arrowback" />
                        </PagingButton>
                    </Pagination>
                    <Gap />
                    {!inlineEditorOpen && !isReloading && (
                        <PlayContainer
                            scaleAlert={this.scaleAlert}
                            viewProps={viewProps}
                            publicView={publicView}
                            plan={plan}
                            taskIndex={taskIndex}
                            flowPage={flowPage}
                            changeFlowPage={this.changeFlowPage}
                        />
                    )}
                    <EditorContainer
                        isScaleAlert={isScaleAlert}
                        viewProps={viewProps}
                        publicView={publicView}
                        toggleInlineEditor={this.toggleInlineEditor}
                        plan={plan}
                        taskIndex={taskIndex}
                        inlineEditorOpen={inlineEditorOpen}
                        playable={playable}
                        reload={this.reload}
                        flowPage={flowPage}
                        changeFlowPage={this.changeFlowPage}
                    />
                </Wrapper>
            </Editor>
        );
    }
}

export default Task;
